import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import "./ValveTypes.css";

const defaultForm = { name: "" };

function ValveTypes() {
  const [valveTypes, setValveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editValveType, setEditValveType] = useState(null); // valve type being edited (name edit)
  const [valveToDelete, setValveToDelete] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [message, setMessage] = useState({ type: "", text: "" });
  const navigate = useNavigate();

  useEffect(() => {
    fetchValveTypes();
  }, []);

  const fetchValveTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/valve-types");
      setValveTypes(response.data.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        showMessage("error", "Session expired. Please login again.");
      } else {
        showMessage("error", error.response?.data?.message || "Failed to load valve types");
      }
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  // Opens the "Add New Valve Type" inline popup
  const openAddModal = () => {
    setEditValveType(null);
    setFormData(defaultForm);
    setShowModal(true);
  };

  // Opens the "Edit Valve Type Name" inline popup
  const openEditModal = (valveType) => {
    setEditValveType(valveType);
    setFormData({ name: valveType.name });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editValveType) {
        await axios.put(`/valve-types/${editValveType.id}`, { name: formData.name });
        showMessage("success", "Valve type updated successfully");
      } else {
        await axios.post("/valve-types", { name: formData.name });
        showMessage("success", "Valve type created successfully");
      }
      setShowModal(false);
      await fetchValveTypes();
    } catch (error) {
      if (error.response?.status === 401) {
        showMessage("error", "Session expired. Please login again.");
      } else {
        showMessage("error", error.response?.data?.message || "Error occurred");
      }
      // Don't close modal on error so user can see the error message and retry
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/valve-types/${valveToDelete.id}`);
      showMessage("success", "Valve type deleted successfully");
      fetchValveTypes();
    } catch (error) {
      if (error.response?.status === 401) {
        showMessage("error", "Session expired. Please login again.");
      } else {
        showMessage("error", error.response?.data?.message || "Failed to delete valve type");
      }
    } finally {
      setValveToDelete(null);
    }
  };

  return (
    <div className="valve-types-container">
      <div className="valve-types-header">
        <div>
          <h1 className="valve-types-title">Valve Configuration</h1>
          <p className="valve-types-subtitle">Valve Type List</p>
        </div>
        <button className="btn-add-valve" onClick={openAddModal}>
          + Add New Valve Type
        </button>
      </div>

      {message.text && (
        <div className={`alert-message alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="loading-container">Loading valve types...</div>
      ) : (
        <div className="table-card">
          <div className="table-responsive">
            <table className="valve-types-table">
              <thead>
                <tr>
                  <th>Valve Type Name</th>
                  <th>No. of Parameters</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {valveTypes.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="no-data-row">
                      No valve types found
                    </td>
                  </tr>
                ) : (
                  valveTypes.map((valveType) => (
                    <tr key={valveType.id}>
                      <td className="valve-name-cell">{valveType.name}</td>
                      <td>
                        <span className="parameter-count-badge">
                          {valveType.parameter_count}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          {/* Edit → navigates to Screen 2: Parameter List for this valve type */}
                          <button
                            className="btn-edit"
                            onClick={() =>
                              navigate(`/admin/valve-configuration/${valveType.id}/parameters`)
                            }
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => setValveToDelete(valveType)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Valve Type Popup (3.A.1A) */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              {editValveType ? "Edit Valve Type" : "Add New Valve Type"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  Valve Type Name <span className="required-mark">*</span>
                </label>
                <input
                  className="form-input"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Floating Ball Valve"
                  required
                  maxLength={100}
                  autoFocus
                />
                <span className="help-text">
                  Maximum 100 characters. Must be unique (case-insensitive).
                </span>
              </div>
              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  type="button"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button className="btn-submit" type="submit">
                  {editValveType ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {valveToDelete && (
        <div className="modal-overlay" onClick={() => setValveToDelete(null)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h2 className="confirm-title">Confirm Deletion</h2>
            <p className="confirm-text">
              Are you sure you want to delete the valve type "
              <strong>{valveToDelete.name}</strong>"?
            </p>
            {valveToDelete.parameter_count > 0 && (
              <div className="confirm-warning">
                This valve type is used in existing TCDS records or has parameters. Deletion may be blocked.
              </div>
            )}
            <div className="confirm-actions">
              <button
                className="btn-cancel"
                onClick={() => setValveToDelete(null)}
              >
                Cancel
              </button>
              <button className="btn-confirm-delete" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ValveTypes;
